import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Space,
  Stack,
} from '@tonic-one/react';
import { Parser } from '@json2csv/plainjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogApi } from '@Apis';

import { reportPendo } from '../../../utils/setupPendo';
import { useLogReducerContext } from '../context';
import { LogTypeEnum } from '@Types/log';
import { renderData } from '../utils/contentListHandle';
import { generateContentListParams } from '../utils/logStateHandle';
import { isInUIC } from '@Utils/common';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';

const DEFAULT_EXPORT_LIMIT = 10000;

interface Props {
  visible: boolean;
  onClose: () => void;
  columns: any[];
}

const ExportModal = (props: Props) => {
  const { visible, onClose, columns } = props;
  const [exportType, setExportType] = useState('');
  const [exportBtnDisabled, setExportBtnDisabled] = useState(true);
  const { t } = useTranslation('log');
  const { state } = useLogReducerContext();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();

  // log_export_extension, when enabled via the policy service, holds the
  // per-tenant row cap for the Log "All records" export (e.g. 30000). Fall back
  // to the legacy 10000 when absent / disabled / invalid.
  const exportLimit = (() => {
    const configured = selectedOrgGlobalSetting?.logExportExtension;
    return typeof configured === 'number' && configured > 0
      ? configured
      : DEFAULT_EXPORT_LIMIT;
  })();

  const onRadioChanged = (value: string) => {
    setExportType(value);
    setExportBtnDisabled(false);
  };

  const reset = () => {
    setExportType('');
    setExportBtnDisabled(true);
  };

  const closeModal = () => {
    onClose();
    reset();
  };

  const exportCSVFile = (data: any) => {
    const csv = new Parser().parse(data);
    const url = window.URL.createObjectURL(
      new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    );
    // Prevent CSP issue in Firefox
    const doc = isInUIC() ? parent.document : document;
    const link = doc.createElement('a');
    link.href = url;
    link.download = 'result.csv';
    doc.body.appendChild(link);
    link.click();
    doc.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportLog = async () => {
    closeModal();
    if (exportType === '0') {
      const csvData = parseData(state.logs, columns, t);
      exportCSVFile(csvData);
    } else {
      const param = generateContentListParams(state);
      param.page = {
        start: 1,
        size: exportLimit,
      };
      LogApi.getAllLog(param)
        .then((r) => {
          const logs = renderData(r.data.contentList, state.type);
          const csvData = parseData(logs, columns, t);
          exportCSVFile(csvData);
        })
        .catch((err) => {
          window.showNotification({
            titleMsg: t('errors.log.export.title'),
            bodyMsg: t('errors.log.export.body'),
            severity: 'error',
          });
          console.error(err);
        });
    }
    reportPendo('Log.ExportLogData.Export.Click', {
      exportType,
    });
  };

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={visible}
      onClose={closeModal}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('log.modals.export.title')}</ModalHeader>
        <ModalBody>
          <Box>
            <RadioGroup value={exportType} onChange={onRadioChanged}>
              <Stack spacing="1x" shouldWrapChildren>
                <Radio value="0">{t('log.modals.export.currentView')}</Radio>
                {/* url tracking log can not export all records */}
                {state.type !== LogTypeEnum.URLTRACKING && (
                  <Radio value="1">
                    {t('log.modals.export.allRecordsWithLimit', {
                      count: exportLimit.toLocaleString(),
                    })}
                  </Radio>
                )}
              </Stack>
            </RadioGroup>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            minWidth="20x"
            variant="primary"
            disabled={exportBtnDisabled}
            onClick={exportLog}
          >
            {t('log.button.export')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={closeModal}>
            {t('log.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
export default ExportModal;

const parseData = (data: any[], columns: any[], t: any) => {
  const exportData = [];
  const emptyData: { [type: string]: string } = {};
  for (const column of columns) {
    const i18nKey = column.meta?.i18nKey || `log.fields.${column.id}`;
    const headerText = t(i18nKey);
    emptyData[headerText] = '';
  }
  for (const item of data) {
    const rowData = { ...emptyData };
    for (const column of columns) {
      const i18nKey = column.meta?.i18nKey || `log.fields.${column.id}`;
      const headerText = t(i18nKey);
      rowData[headerText] = item[column.id];
    }
    exportData.push(rowData);
  }
  if (exportData.length === 0) {
    exportData.push(emptyData);
  }
  return exportData;
};
